import { Check, ScrollText, Shield } from 'lucide-react';
import type { BillboardGuideline } from '@/features/billboards/api/billboardApi';
import { ExpandableCopy } from '@/features/billboards/components/detail/ExpandableCopy';

type GuidelinesSectionProps = {
  title: string;
  subtitle: string;
  items?: BillboardGuideline[];
  variant?: 'government' | 'screen';
};

function isVisibleGuideline(item: BillboardGuideline) {
  if (item.isAdminApproved === false) return false;
  if (item.status && item.status.toLowerCase() !== 'active') return false;
  return Boolean(item.title?.trim() || item.description?.trim());
}

export function GuidelinesSection({
  title,
  subtitle,
  items,
  variant = 'screen',
}: GuidelinesSectionProps) {
  const visible = (items || []).filter(isVisibleGuideline);
  if (!visible.length) return null;

  const Icon = variant === 'government' ? Shield : ScrollText;
  const iconWrap =
    variant === 'government'
      ? 'bg-[#DFE9FA] text-[#3B66B8]'
      : 'bg-[#EDE4F8] text-[#8156B4]';

  /* Modified by: Ishan | Soft-contrast guidelines card */
  return (
    <section className="bg-white rounded-[24px] p-[22px] shadow-[0_10px_30px_rgba(60,40,120,0.05)] border border-[#EEEBF8]">
      <div className="mb-4 flex items-start justify-between gap-3 sm:mb-5">
        <div className="flex min-w-0 items-start gap-3">
          <div
            className={`mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${iconWrap}`}
          >
            <Icon size={16} strokeWidth={2.1} />
          </div>
          <div className="min-w-0 text-left">
            <h2 className="m-0 text-[18px] font-extrabold tracking-tight text-[#3A3660]">
              {title}
            </h2>
            <p className="mt-0.5 mb-0 text-[13px] text-[#6A6688]">{subtitle}</p>
          </div>
        </div>
        <span className="shrink-0 rounded-full bg-[#F3F2F9] px-2.5 py-1 text-xs font-bold text-[#6A6688]">
          {visible.length}
        </span>
      </div>

      <ul className="m-0 flex list-none flex-col gap-3 p-0">
        {visible.map((item, index) => (
          <li
            key={item.id || item._id || `${item.title}-${index}`}
            className="flex gap-3 border-t border-[#F3F1FA] pt-3.5 first:border-t-0 first:pt-0"
          >
            <div className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-[#EDE4F8] text-[#8156B4]">
              <Check size={12} strokeWidth={3} />
            </div>
            <div className="min-w-0 flex-1 text-left">
              {item.title ? (
                <h3 className="m-0 mb-1 text-[14px] font-bold text-[#3A3660]">
                  {item.title}
                </h3>
              ) : null}
              <ExpandableCopy
                text={item.description}
                fallback="No description provided."
                maxLength={140}
              />
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
